import { Fragment, useEffect, useState } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  InformationCircleIcon,
  TruckIcon,
  ArchiveBoxIcon,
  UserIcon,
  Squares2X2Icon,
  BuildingOffice2Icon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import AuthService from "../services/auth.service";
import LogoComponent from "./logo";

const navigation = [
  { name: "Home", href: "home", current: true, icon: HomeIcon },
  {
    name: "About",
    href: "about-us",
    current: false,
    icon: InformationCircleIcon,
  },
  {
    name: "Track Parcel",
    href: "track_parcel",
    current: false,
    icon: TruckIcon,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const parcelsHash = {
  name: "Parcels",
  href: "parcels",
  current: false,
  icon: ArchiveBoxIcon,
};

export const Header = () => {
  const [currentUser, setCurrentUser] = useState(AuthService.getCurrentUser());

  useEffect(() => {
    // Listen for auth changes
    const handleAuthChange = (event) => {
      if (event.detail.type === "login") {
        setCurrentUser(AuthService.getCurrentUser());
      } else if (event.detail.type === "logout") {
        setCurrentUser(null);
      }
    };

    window.addEventListener("auth-change", handleAuthChange);

    // Update navigation based on user state
    if (currentUser) {
      if (!navigation.includes(parcelsHash)) {
        navigation.push(parcelsHash);
      }
    } else {
      if (navigation.includes(parcelsHash)) {
        navigation.push(parcelsHash);
      }
    }

    // Cleanup listener
    return () => {
      window.removeEventListener("auth-change", handleAuthChange);
    };
  }, [currentUser]);

  const logOut = () => {
    AuthService.logout();
    window.location.replace("/");
  };

  return (
    <Disclosure as="nav" className="bg-white border-b border-gray-100 sticky top-0 z-50">
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
            <div className="relative flex p-2 items-center">
              {/* Mobile menu button */}
              <div className="flex items-center sm:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none transition-colors">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-5 w-5" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>

              {/* Logo + Nav links */}
              <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                <div className="flex flex-shrink-0 items-center">
                  <a href="/" className="flex flex-col items-center">
                    <LogoComponent placement="header" />
                  </a>
                </div>
                <div className="hidden sm:ml-8 sm:flex sm:items-center sm:space-x-1">
                  {navigation.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className={classNames(
                        item.current
                          ? "text-violet-600 font-semibold"
                          : "text-gray-500 hover:text-gray-900",
                        "flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors",
                      )}
                      aria-current={item.current ? "page" : undefined}
                    >
                      <item.icon className="h-4 w-4" aria-hidden="true" />
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>

              {/* Right side actions */}
              <div className="flex items-center gap-2 sm:ml-6">
                {!currentUser && (
                  <>
                    <a
                      href="/login"
                      className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <ArrowLeftOnRectangleIcon
                        className="h-4 w-4"
                        aria-hidden="true"
                      />
                      Login
                    </a>
                    <a
                      href="/signup"
                      className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 px-4 py-2 rounded-lg transition-colors"
                    >
                      <UserPlusIcon className="h-4 w-4" aria-hidden="true" />
                      Sign up
                    </a>
                  </>
                )}

                {/* Profile dropdown */}
                {currentUser && (
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center gap-2 rounded-lg p-1 hover:bg-gray-50 transition-colors focus:outline-none">
                      <span className="sr-only">Open user menu</span>
                      <img
                        className="h-8 w-8 rounded-full object-cover ring-2 ring-gray-100"
                        src={
                          currentUser.user.profile_pic_url ||
                          "//ssl.gstatic.com/accounts/ui/avatar_2x.png"
                        }
                        alt=""
                      />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-44 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-gray-100 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href="/profile"
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors",
                              )}
                            >
                              <UserIcon
                                className="h-4 w-4 text-gray-400"
                                aria-hidden="true"
                              />
                              Your Profile
                            </a>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href="/dashboard"
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors",
                              )}
                            >
                              <Squares2X2Icon
                                className="h-4 w-4 text-gray-400"
                                aria-hidden="true"
                              />
                              Dashboard
                            </a>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href={currentUser?.user?.businesses?.length > 0 ? "/business/dashboard" : "/not-a-business"}
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors",
                              )}
                            >
                              <BuildingOffice2Icon
                                className="h-4 w-4 text-gray-400"
                                aria-hidden="true"
                              />
                              {currentUser?.user?.businesses?.length > 0 ? "Business" : "Register Business"}
                            </a>
                          )}
                        </Menu.Item>
                        <div className="my-1 border-t border-gray-100" />
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href="#"
                              onClick={logOut}
                              className={classNames(
                                active ? "bg-gray-50" : "",
                                "flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:text-red-600 transition-colors",
                              )}
                            >
                              <ArrowRightOnRectangleIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                              Sign out
                            </a>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}
              </div>
            </div>
          </div>

          {/* Mobile nav panel */}
          <Disclosure.Panel className="sm:hidden border-t border-gray-100">
            <div className="space-y-1 px-3 py-3">
              {navigation.map((item) => (
                <Disclosure.Button
                  key={item.name}
                  as="a"
                  href={item.href}
                  className={classNames(
                    item.current
                      ? "text-violet-600 bg-violet-50 font-semibold"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                    "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  )}
                  aria-current={item.current ? "page" : undefined}
                >
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  {item.name}
                </Disclosure.Button>
              ))}
              {!currentUser && (
                <div className="flex gap-2 pt-2 border-t border-gray-100 mt-1">
                  <a
                    href="/login"
                    className="flex flex-1 justify-center items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <ArrowLeftOnRectangleIcon
                      className="h-4 w-4"
                      aria-hidden="true"
                    />
                    Login
                  </a>
                  <a
                    href="/signup"
                    className="flex flex-1 justify-center items-center gap-1.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 px-3 py-2 rounded-lg transition-colors"
                  >
                    <UserPlusIcon className="h-4 w-4" aria-hidden="true" />
                    Sign up
                  </a>
                </div>
              )}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};
